import { useEffect, useMemo, useRef } from 'react'
import styles from './DisplayTypePicker.module.css'
import { DISPLAY_TYPE_OPTIONS } from './displayTypeOptions'

/**
 * DisplayTypePicker renders an 11-card grid of feed display types. Each card
 * shows a small CSS motif so operators can see the layout shape at a glance.
 *
 * Controlled component: parent owns the selected display_type slug.
 */

const MOTIFS = {
  story_block: StoryBlockMotif,
  carousel: CarouselMotif,
  grid: GridMotif,
  hero: HeroMotif,
  circle_stories: CircleStoriesMotif,
  floating_player: FloatingPlayerMotif,
  player_deck: PlayerDeckMotif,
  horizontal_player: HorizontalPlayerMotif,
  horizontal_carousel: HorizontalCarouselMotif,
  dynamic_carousel: DynamicCarouselMotif,
  vertical_stories: VerticalStoriesMotif,
}

const OPTIONS = DISPLAY_TYPE_OPTIONS.map((option) => ({
  ...option,
  Motif: MOTIFS[option.value],
}))

export function DisplayTypePicker({ value, onChange, id, allowedValues = null }) {
  const options = useMemo(() => (
    Array.isArray(allowedValues)
      ? OPTIONS.filter((option) => allowedValues.includes(option.value))
      : OPTIONS
  ), [allowedValues])
  const groupRef = useRef(null)
  const activeIndex = Math.max(
    0,
    options.findIndex((option) => option.value === value),
  )

  const focusCard = (index) => {
    const card = groupRef.current?.querySelector(`[data-index="${index}"]`)
    if (card instanceof HTMLElement) {
      card.focus()
    }
  }

  const handleKeyDown = (event, index) => {
    const cols = 3
    let next = null
    switch (event.key) {
      case 'ArrowRight':
        next = Math.min(options.length - 1, index + 1)
        break
      case 'ArrowLeft':
        next = Math.max(0, index - 1)
        break
      case 'ArrowDown':
        next = Math.min(options.length - 1, index + cols)
        break
      case 'ArrowUp':
        next = Math.max(0, index - cols)
        break
      case 'Home':
        next = 0
        break
      case 'End':
        next = options.length - 1
        break
      default:
        return
    }
    if (next !== null) {
      event.preventDefault()
      onChange(options[next].value)
      focusCard(next)
    }
  }

  useEffect(() => {
    // Keep DOM activeIndex bookkeeping consistent when the parent swaps value
    // out-of-band (e.g. clearing the form). No-op if nothing is focused.
    const active = document.activeElement
    if (active && groupRef.current?.contains(active)) {
      focusCard(activeIndex)
    }
  }, [activeIndex])

  return (
    <div
      ref={groupRef}
      role="radiogroup"
      aria-label="Display type"
      id={id}
      className={styles.grid}
      data-testid="display-type-picker"
    >
      {options.map((option, index) => {
        const selected = option.value === value
        const Motif = option.Motif
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            data-index={index}
            data-value={option.value}
            data-ready={option.ready ? 'true' : 'false'}
            tabIndex={selected || (!value && index === 0) ? 0 : -1}
            className={`${styles.card} ${selected ? styles.cardSelected : ''}`}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => handleKeyDown(event, index)}
          >
            <span aria-hidden="true" className={styles.motifWrap}>
              <Motif />
            </span>
            <span className={styles.cardBody}>
              <strong className={styles.cardLabel}>{option.label}</strong>
              <span className={styles.cardDescription}>{option.description}</span>
            </span>
          </button>
        )
      })}
    </div>
  )
}

// ----- Motifs ---------------------------------------------------------------

function StoryBlockMotif() {
  return (
    <span className={`${styles.motif} ${styles.storyBlock}`}>
      <span className={styles.storyBlockCard}>
        <span className={styles.storyBlockAvatar} />
      </span>
      <span className={styles.storyBlockCard} />
      <span className={styles.storyBlockCard} />
    </span>
  )
}

function CarouselMotif() {
  return (
    <span className={`${styles.motif} ${styles.carousel}`}>
      <span className={styles.carouselCardBack} />
      <span className={styles.carouselCardFront} />
      <span className={styles.carouselCardBack} />
    </span>
  )
}

function GridMotif() {
  return (
    <span className={`${styles.motif} ${styles.grid2x3}`}>
      {Array.from({ length: 6 }, (_, index) => (
        <span key={index} className={styles.gridTile} />
      ))}
    </span>
  )
}

function HeroMotif() {
  return (
    <span className={`${styles.motif} ${styles.hero}`}>
      <span className={styles.heroCard} />
      <span className={styles.heroDots}>
        <span />
        <span className={styles.heroDotActive} />
        <span />
      </span>
    </span>
  )
}

function CircleStoriesMotif() {
  return (
    <span className={`${styles.motif} ${styles.circleStories}`}>
      {Array.from({ length: 5 }, (_, index) => (
        <span key={index} className={styles.storyCircle} />
      ))}
    </span>
  )
}

function FloatingPlayerMotif() {
  return (
    <span className={`${styles.motif} ${styles.floatingPlayer}`}>
      <span className={styles.floatingPlayerMain} />
      <span className={styles.floatingPlayerPip} />
    </span>
  )
}

function PlayerDeckMotif() {
  return (
    <span className={`${styles.motif} ${styles.playerDeck}`}>
      <span className={`${styles.deckCard} ${styles.deckCard1}`} />
      <span className={`${styles.deckCard} ${styles.deckCard2}`} />
      <span className={`${styles.deckCard} ${styles.deckCard3}`} />
    </span>
  )
}

function HorizontalPlayerMotif() {
  return (
    <span className={`${styles.motif} ${styles.horizontalPlayer}`}>
      <span className={styles.horizontalPlayerCard}>
        <span className={styles.playIcon} />
      </span>
    </span>
  )
}

function HorizontalCarouselMotif() {
  return (
    <span className={`${styles.motif} ${styles.horizontalCarousel}`}>
      {Array.from({ length: 3 }, (_, index) => (
        <span key={index} className={styles.hcarouselCard} />
      ))}
    </span>
  )
}

function DynamicCarouselMotif() {
  return (
    <span className={`${styles.motif} ${styles.dynamicCarousel}`}>
      <span className={`${styles.dynCard} ${styles.dynCardBig}`} />
      <span className={styles.dynCardSmallStack}>
        <span className={`${styles.dynCard} ${styles.dynCardSmall}`} />
        <span className={`${styles.dynCard} ${styles.dynCardSmall}`} />
      </span>
    </span>
  )
}

function VerticalStoriesMotif() {
  return (
    <span className={`${styles.motif} ${styles.verticalStories}`}>
      <span className={styles.phoneFrame}>
        <span className={styles.phoneNotch} />
        <span className={styles.phoneDots}>
          <span />
          <span className={styles.heroDotActive} />
          <span />
          <span />
        </span>
      </span>
    </span>
  )
}

export default DisplayTypePicker
